import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { AppStateEnum } from '../../../../main/types/app-state.types.ts';
import { BallMover, StepType, TaskType } from '../../../../main/types/asset/asset.enums.ts';
import { LessonStep, LessonTask } from '../../../../main/types/asset/lesson.types.ts';
import { getLessonsUtil } from '../../../../main/utils/asset/lessons.util.ts';
import { getAudioPlayerUtil } from '../../../../main/utils/audio/audio-player.util.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../../main/utils/monitoring/sentry.util.ts';
import { getReduxStoreUtil } from '../../../../main/utils/redux-store.util.ts';

import { CharacterBubbleAnimation } from '../../../constants/character-bubble-animations.ts';
import { SPEECH_RECOGNITION_MESSAGES } from '../../../constants/speech-recognition.ts';
import { selectCurrentLessonCharacter } from '../../../selectors/current-lesson-character.selector.ts';
import { selectCurrentLessonTaskStepKey } from '../../../selectors/current-lesson-task-step-key.selector.ts';
import { selectCurrentLessonTaskStep } from '../../../selectors/current-lesson-task-step.selector.ts';
import { selectCurrentStepType } from '../../../selectors/current-step-type.selector.ts';
import { selectCurrentTaskType } from '../../../selectors/current-task-type.selector.ts';
import { selectNextStep } from '../../../selectors/next-step.selector.ts';
import { selectStorySentenceBallAnimationSequence } from '../../../selectors/story-sentence-ball-animation-sequence.selector.ts';
import { triggerCharacterBubbleAnimationThunk } from '../../../slices/app-state/thunks/trigger-character-bubble-animation.thunk.ts';
import { updateAppStateThunk } from '../../../slices/app-state/thunks/update-app-state.thunk.ts';
import {
  resetBallPosition,
  setBallIsVisible,
  setupBallAnimation,
  completeBallAnimation,
  positionBallAtWord,
} from '../../../slices/ball/ball.slice.ts';
import { navigateToNextStepThunk } from '../../../slices/lesson/thunks/navigate-to-next-step.thunk.ts';
import { completeStepThunk } from '../../../slices/progress/thunks/complete-step.thunk.ts';
import { trackProgressionEventThunk } from '../../../slices/progress/thunks/track-progression-event.thunk.ts';
import { resetReplacements } from '../../../slices/scripts/scripts.slice.ts';
import { disableDrawing, resetUserWritingPath } from '../../../slices/sounds-writing/sounds-writing.slice.ts';
import {
  STORY_READING_LESSON_TASK_TYPES,
  SOUNDS_WITH_ARROW_LESSON_TASK_TYPES,
  CAPITAL_LETTERS_LESSON_TASK_TYPES,
} from '../../../types/task.types.ts';

import { getOpenResponseGraderUtil } from '../../graders/open-response-grader.util.ts';
import { getPronunciationGraderFactory } from '../../graders/pronunciation-grader-factory.util.ts';
import { getHelpUtil } from '../../managers/help.util.ts';
import { getIncorrectAttemptsUtil } from '../../managers/incorrect-attempts.util';

export abstract class BaseAppStateChangeHandlerUtil {
  private readonly methodMap: Record<AppStateEnum, () => Promise<void>> = {
    [AppStateEnum.Aborting]: this.onAborting.bind(this),
    [AppStateEnum.Booting]: this.onBooting.bind(this),
    [AppStateEnum.ReadyToStart]: this.onReadyToStart.bind(this),
    [AppStateEnum.Playing]: this.onPlaying.bind(this),
    [AppStateEnum.Writing]: this.onWriting.bind(this),
    [AppStateEnum.Recording]: this.onRecording.bind(this),
    [AppStateEnum.Evaluating]: this.onEvaluating.bind(this),
    [AppStateEnum.AwaitingTap]: this.onAwaitingTap.bind(this),
    [AppStateEnum.ReceivingDrawing]: this.onReceivingDrawing.bind(this),
    [AppStateEnum.EvaluatingDrawing]: this.onEvaluatingDrawing.bind(this),
    [AppStateEnum.Correct]: this.onCorrect.bind(this),
    [AppStateEnum.AssumeCorrect]: this.onAssumeCorrect.bind(this),
    [AppStateEnum.Incorrect]: this.onIncorrect.bind(this),
    [AppStateEnum.Retry]: this.onRetry.bind(this),
    [AppStateEnum.ReadyForNextStep]: this.onReadyForNextStep.bind(this),
    [AppStateEnum.MoveToNextStep]: this.onMoveToNextStep.bind(this),
    [AppStateEnum.Helping]: this.onHelping.bind(this),
    [AppStateEnum.CollectingFeedback]: this.onCollectingFeedback.bind(this),
    [AppStateEnum.CollectingParentInfo]: this.onCollectingParentInfo.bind(this),
  } as const;

  protected constructor(
    protected readonly audioPlayerUtil = getAudioPlayerUtil(),
    protected readonly helpUtil = getHelpUtil(),
    protected readonly incorrectAttemptsUtil = getIncorrectAttemptsUtil(),
    protected readonly lessonsUtil = getLessonsUtil(),
    protected readonly openResponseGraderUtil = getOpenResponseGraderUtil(),
    protected readonly pronunciationGraderFactory = getPronunciationGraderFactory(),
    protected readonly reduxStoreUtil = getReduxStoreUtil(),
    protected readonly localeUtil = getLocaleUtil(),
    protected readonly sentryUtil = getSentryUtil(),
    protected readonly dispatch = getReduxStoreUtil().getDispatch()
  ) {}

  public handleAppStateChange(state: AppStateEnum): void {
    this.methodMap[state]?.();
  }

  protected async onAborting(): Promise<void> {
    console.log('onAborting');
    await this.resetBallPosition();
    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(disableDrawing());
    dispatch(resetUserWritingPath());
    this.audioPlayerUtil.stopPlaying();
  }

  protected async onBooting(): Promise<void> {
    console.log('onBooting');
    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(setBallIsVisible(false));
    return Promise.resolve();
  }

  protected async onReadyToStart(): Promise<void> {
    console.log('onReadyToStart');
    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(resetReplacements());
    this.incorrectAttemptsUtil.handleStartStep();
    await this.resetBallPosition();

    return Promise.resolve();
  }

  protected onPlaying(): Promise<void> {
    console.log('onPlaying');
    return Promise.resolve();
  }

  protected onWriting(): Promise<void> {
    console.log('onWriting');
    return Promise.resolve();
  }

  protected onRecording(): Promise<void> {
    console.log('onRecording');
    return Promise.resolve();
  }

  protected async onEvaluating(): Promise<void> {
    console.log('onEvaluating');
    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(setBallIsVisible(true));
  }

  protected onAwaitingTap(): Promise<void> {
    console.log('onAwaitingTap');
    return Promise.resolve();
  }

  protected onReceivingDrawing(): Promise<void> {
    console.log('onReceivingDrawing');
    return Promise.resolve();
  }

  protected onCorrect(): Promise<void> {
    console.log('onCorrect');
    return Promise.resolve();
  }

  protected onAssumeCorrect(): Promise<void> {
    console.log('onAssumeCorrect');
    return Promise.resolve();
  }

  protected onIncorrect(): Promise<void> {
    console.log('onIncorrect');
    return Promise.resolve();
  }

  protected async onRetry(): Promise<void> {
    console.log('onRetry');
    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(resetReplacements());
    this.resetBallPosition();
    return Promise.resolve();
  }

  protected async onReadyForNextStep(): Promise<void> {
    console.log('onReadyForNextStep');
    await this.resetBallPosition();
  }

  protected async onMoveToNextStep(): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(setBallIsVisible(false));
    const shouldBlockAnimation = await this.shouldBlockAnimationsNavigatingToNextStep();
    await dispatch(navigateToNextStepThunk({ blockAnimation: shouldBlockAnimation })).unwrap();
  }

  protected async onHelping(): Promise<void> {
    console.log('onHelping');

    await this.resetBallPosition();
    await this.helpUtil.offerHelp();
  }

  protected async onCollectingFeedback(): Promise<void> {
    console.log('onCollectingFeedback');
    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(setBallIsVisible(false));
  }

  protected onCollectingParentInfo(): Promise<void> {
    console.log('onCollectingParentInfo');
    return Promise.resolve();
  }

  protected onEvaluatingDrawing(): Promise<void> {
    console.log('onEvaluatingDrawing');
    return Promise.resolve();
  }

  // Helper methods

  protected async handleError(error: Error): Promise<void> {
    const state = await this.reduxStoreUtil.getRootState();
    const appState = state.appState.state;
    this.sentryUtil.trackError(error, {
      state: appState,
      component: this.constructor.name,
    });
  }

  protected async handleCorrect({
    assuming = false,
    answer = SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE,
    nextState = AppStateEnum.ReadyForNextStep,
  }: {
    assuming?: boolean;
    answer?: string;
    nextState?: AppStateEnum;
  }): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();

    dispatch(setBallIsVisible(false));
    dispatch(triggerCharacterBubbleAnimationThunk(CharacterBubbleAnimation.REDUCE));
    await dispatch(completeStepThunk()).unwrap();

    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.waitSpeakCompletion();

    dispatch(
      trackProgressionEventThunk({
        event: assuming ? TrackingEventEnum.STEP_ASSUMED_CORRECT : TrackingEventEnum.STEP_COMPLETED_CORRECTLY,
        answer,
      })
    );

    dispatch(updateAppStateThunk({ state: nextState, blockAnimation: true }));
  }

  protected async handleIncorrect(answer: string, incorrectNextState?: AppStateEnum): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();

    const nextState = await this.incorrectAttemptsUtil.handleIncorrectStep();
    if (nextState === AppStateEnum.Correct) {
      await dispatch(updateAppStateThunk({ state: AppStateEnum.AssumeCorrect })).unwrap();
      return;
    }

    await this.audioPlayerUtil.speakStep();
    dispatch(
      trackProgressionEventThunk({
        event: TrackingEventEnum.STEP_COMPLETED_INCORRECTLY,
        answer,
      })
    );
    await this.audioPlayerUtil.waitSpeakCompletion();
    await dispatch(updateAppStateThunk({ state: incorrectNextState ?? nextState, blockAnimation: true })).unwrap();
  }

  protected async handleAppAskStepEvaluation(
    task: LessonTask,
    step: LessonStep,
    recognizedText: string
  ): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();

    const sentenceString = step.answer!;
    const question = step.question!;
    const story = task.story?.flatMap((sentence) => sentence).join(' ') ?? '';

    if (recognizedText === sentenceString.toLowerCase()) {
      await dispatch(updateAppStateThunk({ state: AppStateEnum.Correct, blockAnimation: true })).unwrap();
      return;
    }

    dispatch(updateAppStateThunk({ state: AppStateEnum.Evaluating, blockAnimation: true }));
    const { isCorrect } = await this.openResponseGraderUtil.checkAnswer({
      question,
      story,
      receivedAnswer: recognizedText,
      prototypeAnswer: sentenceString,
    });

    if (isCorrect) {
      await dispatch(updateAppStateThunk({ state: AppStateEnum.Correct, blockAnimation: true })).unwrap();
    } else {
      await dispatch(updateAppStateThunk({ state: AppStateEnum.Incorrect, blockAnimation: true })).unwrap();
    }
  }

  protected async resetBallPosition(): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(resetBallPosition());
  }

  protected async handleInvalidState(): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(updateAppStateThunk({ state: AppStateEnum.ReadyToStart }));
  }

  protected async handleAppReadSentenceWhileAnimatingBall(): Promise<void> {
    // Get the animation sequence for the current sentence
    const dispatch = await this.reduxStoreUtil.getDispatch();
    const state = await this.reduxStoreUtil.getRootState();
    const animationSequence = selectStorySentenceBallAnimationSequence(state);

    // Start speaking and wait for the first part to be played
    this.audioPlayerUtil.speakPronunciation();

    // Start the ball animation sequence
    if (animationSequence.length > 0) {
      const { rowIndex, fromXPosition } = animationSequence[0];
      dispatch(
        positionBallAtWord({
          rowIndex: rowIndex ?? 0,
          xPosition: fromXPosition ?? 0,
          isVisible: true,
        })
      );
      await this.audioPlayerUtil.waitStartSpeaking();
      dispatch(setupBallAnimation({ sequence: animationSequence }));
    }

    console.log('Waiting for speaking to complete');
    // Wait for speaking to complete
    await this.audioPlayerUtil.waitSpeakCompletion();

    // Give an extra 150ms per animation in sequence to ensure the ball is fully animated
    if (animationSequence.length > 0) {
      await new Promise((resolve) => setTimeout(resolve, animationSequence.length * 150));
      dispatch(completeBallAnimation(animationSequence[animationSequence.length - 1].toXPosition ?? 0));
    }
  }

  protected async handleBallAnimationWhileUserReadsSentence(): Promise<void> {
    // Get the current state to check if we should animate the ball
    const state = await this.reduxStoreUtil.getRootState();
    const ballMover = state.lesson.currentStep?.ballMover;

    // If ballMover is not 'user', start the ball animation
    if (ballMover !== BallMover.User) {
      const animationSequence = selectStorySentenceBallAnimationSequence(state);
      const dispatch = await this.reduxStoreUtil.getDispatch();
      if (animationSequence.length > 0) {
        const { rowIndex, fromXPosition } = animationSequence[0];
        dispatch(
          positionBallAtWord({
            rowIndex: rowIndex ?? 0,
            xPosition: fromXPosition ?? 0,
            isVisible: true,
          })
        );
        dispatch(setupBallAnimation({ sequence: animationSequence }));
      }
    }
  }

  protected async handleReadyForNextStep(): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();
    const state = await this.reduxStoreUtil.getRootState();
    const { lesson, task, step } = selectCurrentLessonTaskStep(state);
    const lessonTaskStepKey = selectCurrentLessonTaskStepKey(state);

    if (!lesson || !step || !task || lessonTaskStepKey?.step === undefined) {
      this.handleError(new Error('Invalid state'));
      return;
    }

    if (!this.lessonsUtil.isFinalStep(lessonTaskStepKey)) {
      await dispatch(updateAppStateThunk({ state: AppStateEnum.MoveToNextStep })).unwrap();
    } else {
      if (task.taskType !== TaskType.LessonComplete) {
        dispatch(triggerCharacterBubbleAnimationThunk(CharacterBubbleAnimation.SHAKE));
      }

      if (step.stepType !== StepType.AppSay) {
        const audioPlayer = getAudioPlayerUtil();
        await audioPlayer.speakStep();
        await audioPlayer.waitSpeakCompletion();
      }

      dispatch(setBallIsVisible(true));
    }
  }

  protected async handleRetryAfterError(): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();
    const state = await this.reduxStoreUtil.getRootState();
    dispatch(setBallIsVisible(false));
    const character = selectCurrentLessonCharacter(state);
    this.audioPlayerUtil.speak(this.localeUtil.select('learn:retry_after_error'), character);
    await this.audioPlayerUtil.waitSpeakCompletion();
    await dispatch(updateAppStateThunk({ state: AppStateEnum.Retry })).unwrap();
  }

  protected async shouldBlockAnimationsNavigatingToNextStep(): Promise<boolean> {
    const state = await this.reduxStoreUtil.getRootState();
    const nextStep = selectNextStep(state);
    return Boolean(nextStep && nextStep.step.stepType === StepType.UserPronounceFast);
  }

  protected async isOneOfSteps(stepTypes: StepType[]): Promise<boolean> {
    const state = await this.reduxStoreUtil.getRootState();
    const stepType = selectCurrentStepType(state);
    return Boolean(stepType && stepTypes.includes(stepType));
  }

  protected async isOneOfTasks(taskTypes: TaskType[]): Promise<boolean> {
    const state = await this.reduxStoreUtil.getRootState();
    const taskType = selectCurrentTaskType(state);
    return Boolean(taskType && taskTypes.includes(taskType));
  }

  protected async shouldAnimateBall(): Promise<boolean> {
    const state = await this.reduxStoreUtil.getRootState();
    const taskType = selectCurrentTaskType(state);
    return Boolean(
      taskType &&
        (await this.isOneOfTasks([
          ...STORY_READING_LESSON_TASK_TYPES,
          ...SOUNDS_WITH_ARROW_LESSON_TASK_TYPES,
          ...CAPITAL_LETTERS_LESSON_TASK_TYPES,
        ]))
    );
  }

  protected abstract getHandlerName(): string;
}
